import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { designer, marqueeWords } from "../data/portfolio";

const HeadlineToken = ({ token, delay }) => {
  const base = "inline-block";
  const italic = token.tone === "italic-accent";
  return (
    <motion.span
      initial={{ y: 60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`${base} ${italic ? "text-[#E5326A] font-light" : "text-[#0E1A2B] font-light"}`}
    >
      {token.text}
    </motion.span>
  );
};

export const HeroSection = () => {
  const { scrollY } = useScroll();
  const portraitY = useTransform(scrollY, [0, 800], [0, -60]);
  const portraitScale = useTransform(scrollY, [0, 800], [1, 1.08]);

  return (
    <section
      id="top"
      className="relative overflow-hidden pt-24 md:pt-24 lg:pt-28"
      data-testid="hero-section"
    >
      

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          {/* Headline block */}
          <div className="col-span-12 lg:col-span-8">
            <div className="mb-3" data-testid="hero-pretitle">
              <span
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium"
                style={{ background: "#FFFFFF", color: "#0E1A2B", border: "1px solid #E4E6EC" }}
              >
                Lead UI/UX Designer · Mumbai
              </span>
            </div>
            <div className="text-lg md:text-xl text-[#6E7583] mb-2">Hey there, I'm</div>
            <h1
              className="font-display leading-[1] tracking-tight text-[clamp(3rem,7.5vw,7rem)] text-[#E5326A]"
              data-testid="hero-headline"
            >
              Shakti Rai<span className="text-[#0E1A2B]">.</span>
            </h1>
            <h2
              className="mt-6 leading-snug text-xl md:text-2xl lg:text-3xl text-[#0E1A2B] max-w-2xl font-medium"
              data-testid="hero-subhead"
            >
              {designer.headline.map((t, i) => (
                <React.Fragment key={i}>
                  <HeadlineToken token={t} delay={0.25 + i * 0.09} />
                  {t.text.endsWith(" ") ? " " : ""}
                </React.Fragment>
              ))}
            </h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2 }}
              className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[#6E7583]"
              data-testid="hero-intro"
            >
              {designer.intro}
            </motion.p>
          </div>

          {/* Portrait — tilted polaroid */}
          <div className="col-span-12 lg:col-span-4">
            <motion.div
              style={{ y: portraitY, scale: portraitScale }}
              initial={{ opacity: 0, y: 40, rotate: -4 }}
              animate={{ opacity: 1, y: 0, rotate: -4 }}
              transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-[320px] lg:max-w-[360px] mx-auto lg:ml-auto"
              data-testid="hero-portrait-wrap"
            >
              <div
                className="bg-white p-3 pb-14 rounded-md shadow-[0_20px_50px_-20px_rgba(14,26,43,0.25)]"
                style={{ border: "1px solid #E4E6EC" }}
              >
                <img
                  src={designer.portrait}
                  alt={`${designer.name}, ${designer.role}`}
                  className="block w-full aspect-square object-cover rounded-sm"
                  data-testid="hero-portrait-image"
                  loading="eager"
                />
                <div
                  className="absolute left-0 right-0 bottom-4 text-center text-[#0E1A2B]"
                  style={{ fontFamily: "'Caveat', cursive", fontSize: 24, fontWeight: 600 }}
                >
                  Based in Mumbai
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.4 }}
          className="mt-12 md:mt-14 flex flex-wrap items-end justify-between gap-6"
        >
          <div className="flex flex-wrap items-center gap-4 md:gap-5">
            <a
              href="#work"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#work")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-3 text-sm tracking-[0.24em] uppercase text-[#0E1A2B]"
              data-testid="hero-scroll-cta"
            >
              <span className="w-10 h-10 rounded-full border border-[#0E1A2B]/40 flex items-center justify-center group-hover:bg-[#E5326A] group-hover:border-[#E5326A] group-hover:text-[#FFFFFF] transition-all duration-500">
                <ArrowDown size={16} />
              </span>
              Selected work
            </a>

            <a
              href={designer.resume}
              download="Shakti-Rai-Lead-Designer.pdf"
              className="group inline-flex items-center gap-2.5 pl-2 pr-5 py-2 rounded-full bg-[#0E1A2B] text-[#FFFFFF] text-sm tracking-[0.22em] uppercase hover:bg-[#E5326A] transition-colors duration-500"
              data-testid="hero-resume-download"
            >
              <span className="w-8 h-8 rounded-full bg-[#FFFFFF]/15 flex items-center justify-center group-hover:bg-[#FFFFFF]/25 transition-colors duration-500">
                <Download size={14} />
              </span>
              Résumé
            </a>
          </div>

          <div className="hidden md:block text-right">
            <div className="eyebrow mb-1">Currently</div>
            <div className="text-sm text-[#0E1A2B]">
              Open for new opportunities
            </div>
          </div>
        </motion.div>
      </div>

      {/* Marquee */}
      <div className="relative mt-8 md:mt-10 border-y border-[#E4E6EC]/70 bg-[#EFF0F4]/30 py-3 overflow-hidden" data-testid="hero-marquee">
        <div className="marquee font-display text-2xl md:text-3xl italic text-[#0E1A2B]/70">
          {[...marqueeWords, ...marqueeWords].map((w, i) => (
            <span key={i} className="flex items-center gap-16 whitespace-nowrap">
              {w}
              <span className="text-[#E5326A]">✦</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
